/**
 * The piece codes of a set, worked out from its item code.
 *
 * A set is not always two boxes and its code does not always announce them. LSK, 2026-09-04:
 * *"not always AB only, sometimes will have diff pieces, like ABCDEF, but the parent code might be
 * a very normal sku without AB at the behind."* The live data shows all three shapes:
 *
 *   BFJOY152(12)FB1AB    ends with the pieces      → strip `AB`   → …FB1A  / …FB1B
 *   DVQ12FS010A+B        ends with them, joined    → strip `A+B`  → …010A  / …010B
 *   DVMDS182PU1ABWD      does NOT end with them    → strip nothing → …ABWDA / …ABWDB
 *
 * So the suffix comes off only when the code genuinely ends with the pieces being split out —
 * `DVMDS182PU1ABWD` contains `AB` in the middle and stripping it would produce a code that matches
 * nothing. Getting that backwards invents products no tag will carry.
 *
 * Two pieces is the minimum: one piece is not a set, and a repeated letter would generate the same
 * child twice and silently drop one of them.
 *
 * NAMED pieces (LSK, 2026-10-03) — `UPF/LPF/SD` — never strip anything: the whole parent code is
 * kept and each piece goes on behind a hyphen, `…FB12P-UPF`. The strip exists because `…FB1AB`
 * literally IS boxes A and B; a trailing `AB` says nothing about platforms. See `setPieces.ts`.
 */

import { parsePieces, pieceSuffix } from './setPieces.js';

export type SetPieceRow = {
  childSku: string;
  parentSku: string;
  piece: string;
  qtyPerSet: string;
};

/**
 * Read the separate-sets box: one item per line, its pieces in an optional second column.
 *
 * Pasted from Excel, so usually tab-separated — and LSK's sheet may hold the pieces column first
 * (2026-10-03), so the order is worked out: an item code never parses as a list of pieces, and a
 * pieces column always does. Only the FIRST separator splits the line; commas after it separate
 * pieces (`UPF, LPF, SD`), which the old `split(/[\t,;]/, 2)` silently cut to `UPF`.
 */
export function parseSeparateLines(
  text: string,
  defaultPieces: string,
): Array<{ code: string; pieces: string }> {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      // A tab is an Excel column boundary and wins outright — `UPF, LPF, SD⇥CODE` has commas in
      // its first column. Without one, the first separator of any kind splits the line.
      const m = line.includes('\t')
        ? /^([^\t]+)\t+(.+)$/.exec(line)
        : /^([^;,\s]+)[;,\s]+(.+)$/.exec(line);
      if (m === null) return { code: line, pieces: defaultPieces };
      const [first, second] = [m[1]!.trim(), m[2]!.trim()];
      return parsePieces(first).ok && !parsePieces(second).ok
        ? { code: second, pieces: first }
        : { code: first, pieces: second };
    });
}

/** Escape a string for use inside a RegExp — item codes contain `(`, `)`, `+` and `"`. */
const esc = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function setPieceCodes(itemCode: string, pieces = 'AB'): SetPieceRow[] {
  const code = itemCode.trim().toUpperCase();
  if (code === '') return [];

  // "a, b , c", "ABC" and "A/B/C" all mean the same three pieces.
  const parsed = parsePieces(pieces);
  if (!parsed.ok) return [];
  const list = parsed.pieces;

  // Strip the suffix ONLY if the pieces are letters and the code ends with them — plain (`AB`),
  // plus-joined (`A+B`), either optionally behind a hyphen. Anything else keeps its code whole.
  let stem = code;
  if (list.every((p) => p.length === 1)) {
    const suffix = new RegExp(`-?(${esc(list.join(''))}|${esc(list.join('+'))})$`);
    if (suffix.test(code)) stem = code.replace(suffix, '');
  }
  if (stem === '') return [];

  return list.map((piece) => ({
    childSku: `${stem}${pieceSuffix(piece)}`,
    parentSku: code,
    piece,
    qtyPerSet: '1',
  }));
}
